// warning-banner.component.ts
import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ViolationStateServiceTsService } from '../../engine/violation-state.service.ts.service';
import { ProctoringRuleEngineServiceTsService } from '../../engine/proctoring-rule-engine.service.ts.service';

@Component({
	selector: 'app-warning-banner',
	standalone: true,
	imports: [CommonModule],
	template: `
    <ng-container *ngIf="state$ | async as state">

      <div class="warning-overlay" 
           *ngIf="state.currentWarning && !state.isTerminated">
        <div class="warning-box" 
             [class.critical]="isCritical(state.currentWarning)">
          <div class="warning-icon">⚠️</div>
          <div class="warning-content">
            <h3>{{ getTitle(state.currentWarning) }}</h3>
            <p>{{ getMessage(state.currentWarning) }}</p>
            <span class="strike-info" *ngIf="isCritical(state.currentWarning)">
              Strike {{ state.warningCount }} of {{ state.maxWarnings }}
            </span>
          </div>
          <button class="dismiss-btn" (click)="dismiss()">Got it</button>
        </div>
      </div>

      <div class="termination-overlay" *ngIf="state.isTerminated">
        <div class="termination-box">
          <div class="term-icon">❌</div>
          <h2>Session Terminated</h2>
          <p>You have exceeded the maximum proctoring violations.</p>
          <small>Reason: {{ state.terminationReason }}</small>
        </div>
      </div>

    </ng-container>
  `,
	styles: [`
    .warning-overlay {
      position: fixed;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 99999;
      width: 100%;
      max-width: 600px;
      padding: 0 16px;
      animation: slideDown 0.3s ease;
    }
    @keyframes slideDown {
      from { opacity: 0; transform: translateX(-50%) translateY(-20px); }
      to   { opacity: 1; transform: translateX(-50%) translateY(0); }
    }
    .warning-box {
      background: #fff8e1;
      border: 2px solid #ffc107;
      border-radius: 12px;
      padding: 16px 20px;
      display: flex;
      align-items: center;
      gap: 16px;
      box-shadow: 0 8px 30px rgba(0,0,0,0.15);
    }
    .warning-box.critical {
      background: #fff0f0;
      border-color: #dc3545;
    }
    .warning-icon { font-size: 2rem; flex-shrink: 0; }
    .warning-content { flex: 1; }
    .warning-content h3 { margin: 0 0 4px; font-size: 0.95rem; font-weight: 700; }
    .warning-content p  { margin: 0 0 6px; font-size: 0.82rem; color: #555; }
    .strike-info { font-size: 0.75rem; color: #888; font-weight: 600; }
    .dismiss-btn {
      padding: 8px 18px;
      background: #dc3545;
      color: white;
      border: none;
      border-radius: 8px;
      cursor: pointer;
      font-size: 0.85rem;
      font-weight: 600;
    }
    .termination-overlay {
      position: fixed;
      inset: 0;
      background: rgba(0,0,0,0.5);
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 999999;
    }
    .termination-box {
      background: white;
      border-radius: 16px;
      padding: 48px 40px;
      text-align: center;
      max-width: 420px;
      width: 90%;
    }
    .term-icon { font-size: 3rem; margin-bottom: 16px; }
    .termination-box h2 { color: #dc3545; margin: 0 0 12px; }
  `]
})
export class WarningBannerComponent {

	state$ = this.violationState.state$;

	private readonly TITLES: Record<string, string> = {
		NO_FACE: '🚫 No Face Detected',
		MULTIPLE_FACES: '👥 Multiple People Detected',
		LOOKING_AWAY: '👀 Look at the Screen',
		EYES_CLOSED: '😴 Eyes Closed',
		FACE_TOO_FAR: '📏 Move Closer to Camera',
		TAB_SWITCH: '🔄 Tab Switch Detected',
		BACKGROUND_VOICE: '🔊 Background Voice Detected'
	};


	private readonly MESSAGES: Record<string, string> = {
		NO_FACE: 'Your face is not visible. Please position yourself in front of the camera.',
		MULTIPLE_FACES: 'Only you should be present during the exam.',
		LOOKING_AWAY: 'Please keep your eyes on the screen at all times.',
		EYES_CLOSED: 'Please keep your eyes open and visible.',
		FACE_TOO_FAR: 'Please move closer so your face fills the camera frame.',
		TAB_SWITCH: 'Switching tabs is not allowed during the exam.',
		BACKGROUND_VOICE: 'A background voice was detected. Please ensure you are alone.'
	};


	constructor(
		private violationState: ViolationStateServiceTsService,
		private ruleEngine: ProctoringRuleEngineServiceTsService
	) { }


	getTitle(type: string | null): string {
		return type ? (this.TITLES[type] || 'Proctoring Warning') : '';
	}


	getMessage(type: string | null): string {
		return type ? (this.MESSAGES[type] || 'A violation was detected.') : '';
	}


	isCritical(type: string | null): boolean {
		return !!type && this.ruleEngine.getSeverity(type) === 'CRITICAL';
	}


	dismiss(): void {
		this.violationState.dismissWarning();
	}

}